import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Alert, alpha, Box, CircularProgress, Container, IconButton, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMicrospecDynamicUIContext } from '../../../../MicrospecDynamicUI/GlobalProvider';
import { getDocById } from '../../../../MicrospecDynamicUI/Helpers/dataUtils';
import { BusinessCategory } from '../../../../MicrospecDynamicUI/Models/Business/BusinessCategory';
import { useMicrospecThemeLanguageContext } from '../../../../MicrospecDynamicUI/Themes';
import { BRANCH_ID, ORG_ID } from '../../../constants/collectionPaths';
import UpsertCategory from './UpsertCategory';

const UpdateCategory: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const { translate } = localizationProvider;
  const { categoryId } = useParams<{ categoryId: string }>();
  const organizationId = ORG_ID;
  const branchId = BRANCH_ID;
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { firestore } = cloudProvider;
  const db = firestore?.instance;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<BusinessCategory | null>(null);

  useEffect(() => {
    const loadCategory = async () => {
      if (!db || !organizationId || !branchId || !categoryId) {
        setError(translate('category.missingParameters'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const collectionPath = `organizations/${organizationId}/branches/${branchId}/categories`;

        const categoryData = await getDocById(
          null,
          null,
          db,
          collectionPath,
          categoryId,
          false // Don't use cache for editing
        );

        if (categoryData) {
          setCategory(categoryData as BusinessCategory);
        } else {
          setError(translate('category.categoryNotFound'));
        }
      } catch (err: any) {
        console.error('Error loading category:', err);
        setError(err.message || translate('category.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    loadCategory();
  }, [db, organizationId, branchId, categoryId, translate]);

  const handleSave = (updatedCategory: BusinessCategory) => {
    window.location.href = '/manager/categories/manage';
  };

  const handleCancel = () => {
    navigate('/manager/categories/manage');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={handleCancel}
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {translate('category.editTitle')}
          </Typography>
        </Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" color="text.secondary">
            {translate('category.unableToLoad')}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!category) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">
          {translate('category.categoryNotFound')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={handleCancel}
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.palette.mode === 'dark'
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            }}
          >
            {translate('category.editTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('category.updateDescription', translate(category.name))}
          </Typography>
        </Box>
      </Box>

      <UpsertCategory
        categoryId={categoryId}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </Container>
  );
};

export default UpdateCategory;
